import is from '@sindresorhus/is';
import { z } from 'zod';

import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import type { ProfileTaskParentContext } from '../model/profile-task';
import {
    isWholeOrNoProfileAddress,
    optionalProfileAddressShape,
    profileAddressMessage,
    segmentIdSchema,
    toProfileAddress,
} from './automation-tile-address';

export const JSA_BUSINESS_IMPACT_TILE_TYPE = 'v1.jsa.business-impact' as const;
export const BUSINESS_IMPACT_TILE_WIDTH = '500px';

/** Mirrors the task tile's address so the explanation can re-read the same task, like the assessment tile does. */
export const businessImpactTileSchema = z
    .object({
        ...optionalProfileAddressShape,
        language: languageCodeSchemaForJSA.optional(),
        taskId: segmentIdSchema,
        taskVersionId: z.string().trim().min(1).optional(),
        type: z.literal(JSA_BUSINESS_IMPACT_TILE_TYPE),
    })
    .refine(isWholeOrNoProfileAddress, { message: profileAddressMessage });

export type BusinessImpactTilePayload = z.infer<typeof businessImpactTileSchema>;

export const createBusinessImpactTilePayload = ({
    context,
    language,
    taskId,
    taskVersionId,
}: {
    /** The job profile the task is listed on; absent for a work task opened on its own. */
    readonly context?: ProfileTaskParentContext;
    readonly language?: LanguageCodeForJSA;
    readonly taskId: string;
    readonly taskVersionId?: string;
}): BusinessImpactTilePayload => ({
    ...toProfileAddress(context),
    ...(is.undefined(language) ? {} : { language }),
    taskId,
    ...(is.undefined(taskVersionId) ? {} : { taskVersionId }),
    type: JSA_BUSINESS_IMPACT_TILE_TYPE,
});
